/*
 * Assignment in the master inbox (docs/2026-10-20-assignment-plan.md, card A4): whether it is on
 * here, who a thread may be assigned to, and the menus that assign one. Assignment is a label and a
 * filter, never a permission: everyone the workspace admits keeps seeing every thread.
 *
 * The counts carry "Mine" when the server supports assignment (`with=assignee`, asked by every
 * list, count and thread read), and say whether it is in use: some open thread assigned to someone.
 * Until the counts answer, the last answer this browser saw for the workspace stands, so the rail's
 * rows are drawn from the first frame instead of arriving with the counts and moving the rows under
 * them.
 */
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { InboxAssignee, InboxConversation, InboxCounts } from "../../src/inbox-v2/contract.ts";
import type { MenuOption } from "../option-menu.tsx";
import { assigneesQuery } from "../queries.ts";

/** The menu's value for nobody: a manual "Unassign", which routing then leaves alone (Q23). */
export const UNASSIGNED = "none";

export type Assignment = {
  /** The viewer, once the people are read. */
  readonly you: string | null;
  /** Who a thread here may be assigned to, by name; empty while they are read. */
  readonly people: readonly InboxAssignee[];
  readonly loading: boolean;
  /**
   * Some open thread here is assigned to someone. Until then Mine is empty and Unassigned is the
   * whole inbox, so the rail draws neither (docs/2026-10-27-inbox-rail-scope-and-counts-plan.md).
   */
  readonly inUse: boolean;
};

const rememberKey = (workspaceId: string) => `lcr-assignment:${workspaceId}`;

/** Assignment in a workspace: off, on with nothing assigned, or on with an open thread assigned. */
type Seen = "off" | "on" | "in_use";

function remembered(workspaceId: string): Seen {
  try {
    const seen = localStorage.getItem(rememberKey(workspaceId));
    return seen === "on" || seen === "in_use" ? seen : "off";
  } catch {
    return "off";
  }
}

function remember(workspaceId: string, seen: Seen): void {
  try {
    if (seen === "off") localStorage.removeItem(rememberKey(workspaceId));
    else localStorage.setItem(rememberKey(workspaceId), seen);
  } catch {
    // A convenience only: without storage the rows wait for the counts.
  }
}

/** The counts' word; undefined until they answer. `all` and `unassigned` count the same open threads. */
function seenIn(counts: InboxCounts | undefined): Seen | undefined {
  if (counts === undefined) return undefined;
  if (counts.mine === undefined || counts.unassigned === undefined) return "off";
  return counts.all > counts.unassigned ? "in_use" : "on";
}

/** Assignment here: the counts' word, or before they answer this browser's last. */
function seenHere(workspaceId: string, counts: InboxCounts | undefined): Seen {
  if (workspaceId === "") return "off";
  return seenIn(counts) ?? remembered(workspaceId);
}

/**
 * Whether this browser last saw an open thread assigned in that workspace, without reading it now:
 * the workspace switcher opens such a workspace on "Mine and unassigned", the list its count is.
 * Elsewhere nothing is assigned, so that list is everyone's and the plain inbox is the same.
 */
export const assignmentInUseRemembered = (workspaceId: string): boolean =>
  remembered(workspaceId) === "in_use";

/** Whether assignment is on here, in use or not. */
export const assignmentOn = (workspaceId: string, counts: InboxCounts | undefined): boolean =>
  seenHere(workspaceId, counts) !== "off";

/** Assignment in this workspace, or null where it is off. */
export function useAssignment(
  workspaceId: string,
  enabled: boolean,
  counts: InboxCounts | undefined,
): Assignment | null {
  const known = seenIn(counts);
  const seen = seenHere(workspaceId, counts);
  const on = enabled && seen !== "off";
  useEffect(() => {
    if (enabled && workspaceId !== "" && known !== undefined) remember(workspaceId, known);
  }, [enabled, workspaceId, known]);
  const people = useQuery({ ...assigneesQuery(workspaceId), enabled: on });
  if (!on) return null;
  return {
    you: people.data?.you ?? null,
    people: people.data?.people ?? [],
    loading: people.isLoading,
    inUse: seen === "in_use",
  };
}

/**
 * The page's Assign: whether it is on here, whether the reading pane's menu or the selection bar's
 * is open, and the write.
 * `targets` are the rows an action without its own rows acts on (the ticked ones, else the open one).
 */
export function useAssignControls(
  workspaceId: string,
  counts: InboxCounts | undefined,
  actions: {
    readonly assign: (
      rows: readonly InboxConversation[],
      assignee: InboxAssignee | null,
      expectedRevision?: number,
    ) => Promise<void>;
  },
  targets: () => readonly InboxConversation[],
  selection: { readonly clear: () => void },
) {
  const assignment = useAssignment(workspaceId, true, counts);
  const [open, setOpen] = useState(false);
  const [bulk, setBulk] = useState(false);
  const close = () => {
    setOpen(false);
    setBulk(false);
  };
  return {
    /** Assignment here; null where it is off. */
    assignment,
    open,
    setOpen,
    bulk,
    setBulk,
    anyOpen: open || bulk,
    close,
    /** M: the bar's menu with rows ticked, else the pane's; where assignment is off, nothing. */
    openFor: (ticked: boolean) => {
      if (!assignment) return;
      if (ticked) setBulk(true);
      else setOpen(true);
    },
    /**
     * Assign to a person (their user id) or to nobody (`UNASSIGNED`). The reading pane passes the
     * revision it showed, so a reassignment made meanwhile is said rather than overwritten.
     */
    assign: async (value: string, rows = targets(), expectedRevision?: number) => {
      close();
      const person = assignment?.people.find((candidate) => candidate.userId === value) ?? null;
      if (value !== UNASSIGNED && !person) return;
      // The toast names the viewer as "you", as the row and the menu do.
      const named =
        person && person.userId === assignment?.you ? { ...person, name: "you" } : person;
      await actions.assign(rows, named, expectedRevision);
      selection.clear();
    },
  };
}

/** How a row and the reading pane name an assignee: "You" for the viewer. */
export function assigneeWord(assignee: InboxAssignee, you: string | null): string {
  return assignee.userId === you ? "You" : assignee.name;
}

/** The menu's choices: the viewer first, then everyone else by name, then nobody. */
export function assignOptions(assignment: Assignment): MenuOption<string>[] {
  const you = assignment.people.find((person) => person.userId === assignment.you);
  const others = assignment.people.filter((person) => person.userId !== assignment.you);
  return [
    ...(you ? [{ value: you.userId, label: `${you.name} (you)` }] : []),
    ...others.map((person) => ({ value: person.userId, label: person.name })),
    { value: UNASSIGNED, label: "Unassigned" },
  ];
}

/** Said under the menu's choices while the people are read: they are on their way, not missing. */
export function PeopleNote(props: { loading: boolean }): ReactNode {
  return props.loading ? (
    <p className="lcr-i2-menu-note" role="status">
      Finding people
    </p>
  ) : null;
}

/**
 * The selection bar's Assign: the same choices as the reading pane's, for every ticked row, with
 * none current. It is the bar's own menu, as Relabel's is (`RelabelMenu`).
 */
export function AssignMenu(props: {
  assignment: Assignment;
  onChoose: (value: string) => void;
  onClose: () => void;
}) {
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => first.current?.focus(), []);
  return (
    <div
      className="lcr-i2-menu"
      role="menu"
      aria-label="Assign to"
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          props.onClose();
        }
      }}
    >
      {assignOptions(props.assignment).map((option, index) => (
        <button
          key={option.value}
          ref={index === 0 ? first : undefined}
          type="button"
          role="menuitem"
          className="lcr-i2-menu-item"
          onClick={() => props.onChoose(option.value)}
        >
          {option.label}
        </button>
      ))}
      <PeopleNote loading={props.assignment.loading} />
    </div>
  );
}
